/**
 * App-wide auth + tab-visibility context. One place fetches the session
 * (GET /api/auth/me) and the tab-visibility map (GET /api/data/settings) so
 * every navigation decision shares one source of truth and one probe per load.
 *
 * Call refresh() after anything that changes the session or tab settings; it
 * resolves when the probe completes so the login flow can `await` it before
 * navigating — otherwise a login-gated route renders before /api/auth/me lands
 * and the guard bounces it back to /login.
 *
 * Both reads are bounded (SESSION_PROBE_TIMEOUT_MS), and refresh() says what
 * the probe found — true signed in, false signed out, null could not tell — so
 * the login page can say why it did not navigate instead of bouncing silently.
 */
import {
  createContext, useCallback, useContext, useEffect, useState, type ReactNode,
} from 'react';

import { SESSION_PROBE_TIMEOUT_MS, timeoutSignal } from '@/lib/fetchTimeout';

/** What one probe of /api/auth/me found: null when it could not be read. */
export type SessionState = boolean | null;

interface AccessState {
  userId: string | null;
  username: string | null;
  authLoading: boolean;
  tabVisibility: Record<string, string>;
  refresh: () => Promise<SessionState>;
}

const AccessContext = createContext<AccessState | null>(null);

export function AccessProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [tabVisibility, setTabVisibility] = useState<Record<string, string>>({});

  // allSettled so a failure of one read (e.g. settings 404 on a fresh deploy)
  // doesn't drop the other.
  const probe = useCallback(async (): Promise<SessionState> => {
    // One signal per read: it bounds the body as well as the headers.
    const [meResult, settingsResult] = await Promise.allSettled([
      fetch('/api/auth/me', {
        credentials: 'include', signal: timeoutSignal(SESSION_PROBE_TIMEOUT_MS),
      }),
      fetch('/api/data/settings', {
        credentials: 'include', signal: timeoutSignal(SESSION_PROBE_TIMEOUT_MS),
      }),
    ]);

    let session: SessionState = null;
    if (meResult.status === 'fulfilled' && meResult.value.ok) {
      try {
        const data = (await meResult.value.json()) as {
          authenticated?: boolean;
          userId?: string;
          username?: string | null;
        };
        if (data.authenticated === false) {
          setUserId(null);
          setUsername(null);
          session = false;
        } else {
          setUserId(data.userId ?? null);
          setUsername(data.username ?? null);
          session = data.userId != null;
        }
      } catch {
        setUserId(null);
        setUsername(null);
      }
    } else {
      setUserId(null);
      setUsername(null);
      // /api/auth/me answers a signed-out session 200 with
      // `authenticated: false`, so a status that is not ok, like a request
      // that never answered, leaves the session unknown.
    }

    if (settingsResult.status === 'fulfilled' && settingsResult.value.ok) {
      try {
        const s = (await settingsResult.value.json()) as
          | { tabVisibility?: Record<string, string> }
          | null;
        const vis = s && typeof s === 'object' ? s.tabVisibility : undefined;
        setTabVisibility(vis && typeof vis === 'object' ? vis : {});
      } catch {
        setTabVisibility({});
      }
    }
    return session;
  }, []);

  // `cancelled` guard only matters for StrictMode's dev double-invoke.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setAuthLoading(true);
      await probe();
      if (!cancelled) setAuthLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [probe]);

  // Deliberately does NOT toggle authLoading, so a re-probe from an
  // already-authed page doesn't flash the route guard's "Checking session…".
  const refresh = useCallback(() => probe(), [probe]);

  return (
    <AccessContext.Provider value={{ userId, username, authLoading, tabVisibility, refresh }}>
      {children}
    </AccessContext.Provider>
  );
}

export function useAccess(): AccessState {
  const ctx = useContext(AccessContext);
  if (!ctx) throw new Error('useAccess must be used within an AccessProvider');
  return ctx;
}
